/**
 * Haptic feedback for mobile: uses Capacitor Haptics on native iOS/Android,
 * falls back to Vibration API on web (where supported).
 */
import { isCapacitorNative } from '@/lib/capacitor';

// Lazy-load Capacitor Haptics so web builds don't require native plugin
let Haptics: typeof import('@capacitor/haptics').Haptics | null = null;
let ImpactStyle: typeof import('@capacitor/haptics').ImpactStyle | null = null;
let NotificationType: typeof import('@capacitor/haptics').NotificationType | null = null;

async function getHaptics() {
  if (Haptics === null && isCapacitorNative()) {
    try {
      const mod = await import('@capacitor/haptics');
      Haptics = mod.Haptics;
      ImpactStyle = mod.ImpactStyle;
      NotificationType = mod.NotificationType;
    } catch {
      // Plugin not available
    }
  }
  return { Haptics, ImpactStyle, NotificationType };
}

function vibrateFallback(pattern: number | number[]) {
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
}

async function runNativeHaptic(
  impact: 'light' | 'medium' | 'heavy' | null,
  notification: 'success' | 'warning' | 'error' | null,
  vibrateMs?: number
) {
  const { Haptics, ImpactStyle, NotificationType } = await getHaptics();
  if (!Haptics) return;
  try {
    if (impact && ImpactStyle) {
      const style =
        impact === 'heavy'
          ? ImpactStyle.Heavy
          : impact === 'medium'
            ? ImpactStyle.Medium
            : ImpactStyle.Light;
      await Haptics.impact({ style });
    } else if (notification && NotificationType) {
      const type =
        notification === 'success'
          ? NotificationType.Success
          : notification === 'error'
            ? NotificationType.Error
            : NotificationType.Warning;
      await Haptics.notification({ type });
    } else if (vibrateMs != null) {
      await Haptics.vibrate({ duration: vibrateMs });
    }
  } catch {
    // Fallback to vibration API if native fails
    if (vibrateMs != null) vibrateFallback(vibrateMs);
    else if (impact === 'heavy') vibrateFallback(25);
    else if (impact === 'medium') vibrateFallback(15);
    else if (impact === 'light') vibrateFallback(5);
  }
}

export const useHaptic = () => {
  return {
    // Task complete – success notification / double-tap "heartbeat"
    triggerSuccess: () => {
      if (isCapacitorNative()) {
        runNativeHaptic(null, 'success');
      } else {
        vibrateFallback([10, 50, 10]);
      }
    },

    // Focus mode start – heavy impact
    triggerHeavy: () => {
      if (isCapacitorNative()) {
        runNativeHaptic('heavy', null);
      } else {
        vibrateFallback(25);
      }
    },

    // Drag pickup – light tick
    triggerLight: () => {
      if (isCapacitorNative()) {
        runNativeHaptic('light', null);
      } else {
        vibrateFallback(5);
      }
    },

    // Drag drop – medium plunk
    triggerDrop: () => {
      if (isCapacitorNative()) {
        runNativeHaptic('medium', null);
      } else {
        vibrateFallback(15);
      }
    },

    // Timer complete – notification warning / rhythmic alarm
    triggerAlarm: () => {
      if (isCapacitorNative()) {
        runNativeHaptic(null, 'warning');
      } else {
        vibrateFallback([500, 200, 500, 200, 500]);
      }
    },

    // Error / validation – error notification
    triggerError: () => {
      if (isCapacitorNative()) {
        runNativeHaptic(null, 'error');
      } else {
        vibrateFallback([50, 50, 50]);
      }
    },

    // Tab / selection change – light impact
    triggerSelection: () => {
      if (isCapacitorNative()) {
        runNativeHaptic('light', null);
      } else {
        vibrateFallback(5);
      }
    },
  };
};

/** Standalone trigger for timer completion (used outside React, e.g. in usePomodoroTimer) */
export function triggerTimerAlarm() {
  if (isCapacitorNative()) {
    getHaptics().then(({ Haptics, NotificationType }) => {
      if (Haptics && NotificationType) {
        Haptics.notification({ type: NotificationType.Warning }).catch(() => {
          vibrateFallback([500, 200, 500, 200, 500]);
        });
      }
    });
  } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate([500, 200, 500, 200, 500]);
  }
}
